import { API_BASE_URL } from '@synergy/config';
import { Box } from '@mui/material';
import { DocumentStatus } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactElement } from 'react';
import urlJoin from 'url-join';

import { MemberPurchaseOrder } from '../models/member-purchase-order';

/**
 * The invoices made from an order, each a link to its PDF — where POOLALPIN
 * bills. A direct supplier sends its own, and the member is told so (#2279).
 * The list's column and the opened row say it the same way; the column, short
 * of room, gives the number alone and the kind of document as its tooltip (R-169).
 */
export const PaloOrderInvoices = ({
    order,
    compact = false,
}: {
    order: MemberPurchaseOrder;
    compact?: boolean;
}): ReactElement => {
    const intl = useIntl();

    // A cancelled order gets no invoice; "noch keine" read as if one were to come (O-15).
    if (DocumentStatus.Canceled === order.status && 0 === order.invoices.length) {
        return <>–</>;
    }

    if (!order.supplierAccount?.isChargingSupplier) {
        return <FormattedMessage id="app.orders.detail.invoice.bySupplier" />;
    }

    if (0 === order.invoices.length) {
        return <FormattedMessage id="app.orders.detail.invoice.open" />;
    }

    return (
        <>
            {order.invoices.map(invoice => {
                const number = invoice.prefixedNumber ?? invoice.invoiceNumber;
                const label = intl.formatMessage(
                    { id: invoice.isCredit ? 'app.orders.detail.credit' : 'app.orders.detail.invoiceNumber' },
                    { number }
                );

                return (
                    <Box key={invoice.invoiceId} sx={{ whiteSpace: 'nowrap' }}>
                        <a
                            title={compact ? label : undefined}
                            href={urlJoin(API_BASE_URL, `/invoices/${invoice.invoiceId}/pdf-invoice`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={event => event.stopPropagation()}
                        >
                            {compact ? number : label}
                        </a>
                    </Box>
                );
            })}
        </>
    );
};
